// Group-scoped broadcasts list page. Mirrors `AdminBroadcastsView`:
// PageHeader + a single CTA ("New broadcast") + the shared `BroadcastsList`.
// The composer lives at /{kind}/{id}/admin/broadcasts/new — same separation
// as /admin/broadcasts vs /admin/broadcasts/new.

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'use-intl';
import { RouterLinkAs } from '@/lib/router-link';
import { Button, DelayedSkeleton, I, PageHeader, PageHeaderSkeleton, Stack } from '@leanin/ui';

import { BroadcastsList } from '@/components/broadcasts/BroadcastsList';
import type {
  BroadcastListStatus,
  BroadcastsPageParams,
} from '@/lib/queries/broadcasts';
import { groupKeys } from '@/lib/queries/groups';
import { spacesKeys } from '@/lib/queries/spaces';

type Props = {
  readonly kind: 'circle' | 'network' | 'space';
  readonly groupId: string;
  // URL state for the broadcast list — threaded through from the group
  // admin route so the filter tab + pagination cursor survive reloads.
  readonly listStatus: BroadcastListStatus;
  readonly listCursor: string | undefined;
};

const ROUTE_BASE: Record<Props['kind'], (groupId: string) => string> = {
  circle: (id) => `/circles/${id}/admin`,
  network: (id) => `/networks/${id}/admin`,
  space: (id) => `/groups/${id}/admin`,
};

export function GroupBroadcastsSection({
  kind,
  groupId,
  listStatus,
  listCursor,
}: Props) {
  const t = useTranslations('Group');
  // Parallel queries, each gated by `kind` via `enabled`. Only the active
  // branch fetches; we use whichever populates `groupName`.
  const circleQuery = useQuery({
    ...groupKeys.circle.detail(groupId),
    enabled: kind === 'circle',
  });
  const networkQuery = useQuery({
    ...groupKeys.network.detail(groupId),
    enabled: kind === 'network',
  });
  const spaceQuery = useQuery({
    ...spacesKeys.detail(groupId),
    enabled: kind === 'space',
  });

  let groupName: string | null = null;
  if (kind === 'circle') {
    const d = circleQuery.data;
    if (d !== undefined && d.access !== 'visitor') groupName = d.circle.name;
  } else if (kind === 'network') {
    const d = networkQuery.data;
    if (d !== undefined) groupName = d.name;
  } else {
    const d = spaceQuery.data;
    if (d !== undefined && d !== null) groupName = d.name;
  }

  const routeBase = ROUTE_BASE[kind](groupId);
  const composerHref = `${routeBase}/broadcasts/new`;

  const buildHref = (params: {
    status?: BroadcastListStatus;
    cursor?: string;
  }) => {
    const usp = new URLSearchParams();
    usp.set('section', 'broadcasts');
    if (params.status && params.status !== 'all') usp.set('status', params.status);
    if (params.cursor) usp.set('cursor', params.cursor);
    return `${routeBase}?${usp.toString()}`;
  };

  const listParams: BroadcastsPageParams = {
    pageSize: 25,
    ...(listStatus !== 'all' ? { status: listStatus } : {}),
    ...(listCursor !== undefined ? { cursor: listCursor } : {}),
  };

  return (
    <Stack gap="lg">
      <DelayedSkeleton
        isLoading={groupName === null}
        fallback={<PageHeaderSkeleton withAction />}
      >
        <PageHeader
          title={t('admin.broadcasts.title')}
          subtitle={t('admin.broadcasts.subtitle', { name: groupName ?? '' })}
          actions={
            <Button
              href={composerHref}
              linkAs={RouterLinkAs}
              variant="primary"
              icon={I.plus}
            >
              {t('admin.broadcasts.newBroadcast')}
            </Button>
          }
        />
      </DelayedSkeleton>
      <BroadcastsList
        scope={{ kind: 'group', groupKind: kind, groupId }}
        listParams={listParams}
        active={listStatus}
        cursor={listCursor}
        buildTabHref={(status) => buildHref({ status })}
        buildPageHref={(cursor) =>
          buildHref({
            ...(listStatus !== 'all' ? { status: listStatus } : {}),
            ...(cursor !== null ? { cursor } : {}),
          })
        }
        historyKey={`group-broadcasts-${kind}-${groupId}`}
      />
    </Stack>
  );
}
